import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { confirmDialog } from 'primereact/confirmdialog';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import useNotify from '../../../hooks/useNotify';
import { FieldError } from '../../../components/novarium/NvTableParts';
import PatientClinicalPanelById from '../../../components/PatientClinicalPanel';
import PatientClinicalHistoryView, { type ClinicalHistoryAction } from '../../../components/PatientClinicalHistory';
import RecipeView from '../../Recipe/RecipeView';
import NewStudyOrderView from '../../StudyOrder/NewStudyOrder/NewStudyOrderView';
import { EStudyOrderSteps } from '../../StudyOrder/StudyOrderEnums';
import MedicalIndicationView from '../../MedicalIndication/MedicalIndicationView';
import StudyResultWizard from '../../StudyResults/components/StudyResultWizard';
import OdontogramView from '../../Odontology/OdontogramView';
import {
  getEditEvolutionInit,
  getEditLog,
  getEvolutionInit,
  markAsMissed,
  returnToQueue,
  saveDraft,
  saveEditEvolution,
  saveEvolution,
} from '../services/AgendaService';
import {
  CIE10Item,
  EditLogEntry,
  EvolutionField,
  EvolutionInitData,
  EvolutionPanel,
  SaveFieldValue,
  SavePanelValue,
} from '../types/evolution.types';
import CIE10ScorePanel from './evolution/CIE10ScorePanel';
import AdministrativeRecordStatus from './evolution/AdministrativeRecordStatus';
import { parseApiError } from '../types/error.types';
import LiveTimer from './shared/LiveTimer';
import './MedicalEvolutionModal.scss';

interface MedicalEvolutionModalProps {
  visible: boolean;
  onHide: () => void;
  patientId: number | null;
  appointmentId: number | null;
  /** ISO timestamp of when attention started — used to sync the timer with the grid */
  servedTime?: string | null;
  /** Edit mode: when set, loads evolution from this patientControlId instead of appointmentId */
  editPatientControlId?: number | null;
  /** Patient name for the minimized bubble */
  patientName?: string | null;
  onAttended?: () => void;
  onMissed?: () => void;
  onCancel?: () => void;
  onEditSaved?: () => void;
  onBookAppointment?: (patientId: number) => void;
}

const TOOLTIP_OPTIONS: TooltipOptions = { className: 'nv-tooltip', event: 'both', position: 'top' };

const SYSTOLIC_SCORE_TYPE = 7;
const DIASTOLIC_SCORE_TYPE = 8;
const GLYCEMIA_SCORE_TYPE = 12;
const INSULIN_SCORE_TYPE = 2363;
const PAM_SCORE_TYPE = 2395;
const WEIGHT_SCORE_TYPE = 1;
const HEIGHT_SCORE_TYPE = 2;
const BMI_SCORE_TYPE = 1200;
const WAIST_SCORE_TYPE = 1201;
const HIP_SCORE_TYPE = 1202;
const WAIST_HIP_INDEX_SCORE_TYPE = 1204;
const WAIST_HEIGHT_INDEX_SCORE_TYPE = 1205;
const NEWS_COPD_SCORE_TYPE = 2100;
const NEWS_SUPPLEMENTAL_OXYGEN_SCORE_TYPE = 2101;
const NEWS_CONSCIOUSNESS_SCORE_TYPE = 2102;
const NEWS_RESULT_SCORE_TYPE = 2103;
const COPD_YES_OPTION_ID = 2100;
const SUPPLEMENTAL_OXYGEN_NO_OPTION_ID = 2103;

const numericFieldValue = (fields: EvolutionField[], scoreTypeId: number): number | null => {
  const raw = fields.find((field) => field.scoreTypeId === scoreTypeId)?.value;
  if (!raw?.trim()) return null;
  const value = Number(raw.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
};

const calculatedValue = (value: number | null, decimals: number): string | null => {
  if (value === null || !Number.isFinite(value)) return null;
  const factor = 10 ** decimals;
  return String(Math.round((value + Number.EPSILON) * factor) / factor);
};

const replaceCalculatedValue = (fields: EvolutionField[], scoreTypeId: number, value: string | null) =>
  fields.map((field) => field.scoreTypeId === scoreTypeId ? { ...field, value } : field);

const calculateAnthropometry = (fields: EvolutionField[]): EvolutionField[] => {
  const weight = numericFieldValue(fields, WEIGHT_SCORE_TYPE);
  const height = numericFieldValue(fields, HEIGHT_SCORE_TYPE);
  const waist = numericFieldValue(fields, WAIST_SCORE_TYPE);
  const hip = numericFieldValue(fields, HIP_SCORE_TYPE);
  let next = replaceCalculatedValue(fields, BMI_SCORE_TYPE,
    calculatedValue(weight && height && weight > 0 && height > 0 ? weight / ((height / 100) ** 2) : null, 1));
  next = replaceCalculatedValue(next, WAIST_HIP_INDEX_SCORE_TYPE,
    calculatedValue(waist && hip && waist > 0 && hip > 0 ? waist / hip : null, 2));
  return replaceCalculatedValue(next, WAIST_HEIGHT_INDEX_SCORE_TYPE,
    calculatedValue(waist && height && waist > 0 && height > 0 ? waist / height : null, 2));
};

const newsNumericScore = (scoreTypeId: number, value: number, fields: EvolutionField[]): number => {
  if (scoreTypeId === 4) return value <= 8 ? 3 : value <= 11 ? 1 : value <= 20 ? 0 : value <= 24 ? 2 : 3;
  if (scoreTypeId === 7) return value <= 90 ? 3 : value <= 100 ? 2 : value <= 110 ? 1 : value < 220 ? 0 : 3;
  if (scoreTypeId === 5) return value <= 40 ? 3 : value <= 50 ? 1 : value <= 90 ? 0 : value <= 110 ? 1 : value <= 130 ? 2 : 3;
  if (scoreTypeId === 3) return value < 35.1 ? 3 : value < 36.1 ? 1 : value < 38.1 ? 0 : value < 39.1 ? 1 : 2;
  if (scoreTypeId === 6) {
    const copd = fields.find((field) => field.scoreTypeId === NEWS_COPD_SCORE_TYPE)?.selectedOptionIds?.[0];
    const oxygen = fields.find((field) => field.scoreTypeId === NEWS_SUPPLEMENTAL_OXYGEN_SCORE_TYPE)?.selectedOptionIds?.[0];
    if (copd === COPD_YES_OPTION_ID) {
      if (value <= 83) return 3;
      if (value <= 85) return 2;
      if (value <= 87) return 1;
      if (value <= 92) return 0;
      if (value === 93 && oxygen === SUPPLEMENTAL_OXYGEN_NO_OPTION_ID) return 0;
      if (value <= 94) return 1;
      if (value <= 96) return 2;
      return 3;
    }
    return value <= 91 ? 3 : value <= 93 ? 2 : value <= 95 ? 1 : 0;
  }
  return 0;
};

const calculateNews2 = (fields: EvolutionField[]): EvolutionField[] => {
  const numericScoreTypes = [4, 6, 7, 5, 3];
  let hasValue = false;
  let result = 0;
  numericScoreTypes.forEach((scoreTypeId) => {
    const value = numericFieldValue(fields, scoreTypeId);
    if (value !== null) {
      hasValue = true;
      result += newsNumericScore(scoreTypeId, value, fields);
    }
  });
  [NEWS_COPD_SCORE_TYPE, NEWS_SUPPLEMENTAL_OXYGEN_SCORE_TYPE, NEWS_CONSCIOUSNESS_SCORE_TYPE].forEach((scoreTypeId) => {
    const field = fields.find((candidate) => candidate.scoreTypeId === scoreTypeId);
    const selectedId = field?.selectedOptionIds?.[0];
    const option = field?.options.find((candidate) => candidate.scoreOptionId === selectedId);
    if (selectedId != null) {
      hasValue = true;
      result += option?.numericValue ?? 0;
    }
  });
  return replaceCalculatedValue(fields, NEWS_RESULT_SCORE_TYPE, hasValue ? String(result) : null);
};

const calculatePanelFields = (beanName: string, fields: EvolutionField[]): EvolutionField[] => {
  if (beanName === 'AnthropometryPanel') return calculateAnthropometry(fields);
  if (beanName === 'News2Panel') return calculateNews2(fields);
  return fields;
};

const clonePanels = (panels: EvolutionPanel[]): EvolutionPanel[] =>
  panels.map((panel) => ({
    ...panel,
    fields: panel.fields.map((field) => ({
      ...field,
      selectedOptionIds: [...(field.selectedOptionIds ?? [])],
      options: [...(field.options ?? [])],
      cie10Items: (field.cie10Items ?? []).map((item) => ({ ...item })),
    })),
  }));


// Los encabezados se montan dentro del header del Dialog, que lleva nv-scope (headerClassName).
function EvolutionDialogHeader({ continuing, editMode, onMinimize }: { continuing: boolean; editMode?: boolean; onMinimize?: () => void }) {
  return (
    <div className="evo-dialog-title">
      <span className="evo-dialog-title__icon" aria-hidden="true">
        <i className={`pi ${editMode ? 'pi-pencil' : 'pi-file-edit'}`} />
      </span>
      <span className="evo-dialog-title__copy">
        <span className="nv-modal-title">
          {editMode ? 'Editar evolución médica' : continuing ? 'Continuar evolución médica' : 'Evolución médica'}
        </span>
        <span className="nv-help evo-dialog-title__subtitle">
          {editMode ? 'Modificar registro de evolución médica' : 'Registro de atención ambulatoria'}
        </span>
      </span>
      {onMinimize ? (
        <Button
          type="button"
          text
          className="nv-icon"
          icon="pi pi-window-minimize"
          aria-label="Minimizar"
          tooltip="Minimizar"
          tooltipOptions={TOOLTIP_OPTIONS}
          onClick={onMinimize}
        />
      ) : null}
    </div>
  );
}

const EMBEDDED_HEADER_CONFIG: Record<ClinicalHistoryAction['type'], { icon: string; title: string; subtitle: string }> = {
  copyRecipe:            { icon: 'pi-file',      title: 'Copiar receta',           subtitle: 'Nueva receta desde copia' },
  copyStudyOrder:        { icon: 'pi-clipboard',  title: 'Copiar orden de estudio', subtitle: 'Nueva orden desde copia' },
  newRecipe:             { icon: 'pi-file',       title: 'Nueva receta',            subtitle: 'Receta digital para el paciente' },
  newStudyOrder:         { icon: 'pi-clipboard',  title: 'Nueva orden de estudio',  subtitle: 'Orden de estudio para el paciente' },
  newMedicalIndication:  { icon: 'pi-list',       title: 'Nueva indicación médica', subtitle: 'Indicación médica para el paciente' },
  loadStudyResult:       { icon: 'pi-file-edit',  title: 'Cargar resultado',        subtitle: 'Incorporar resultado de estudio a la HC' },
  odontogram:            { icon: 'pi-chart-pie',  title: 'Odontología',              subtitle: 'Odontograma clínico del paciente' },
  editEvolution:         { icon: 'pi-pencil',     title: 'Editar evolución',         subtitle: 'Modificar registro de evolución médica' },
  viewEditLog:           { icon: 'pi-history',    title: 'Historial de ediciones',   subtitle: 'Versiones anteriores del registro' },
};

function EmbeddedActionDialogHeader({ actionType, onBack }: { actionType: ClinicalHistoryAction['type']; onBack: () => void }) {
  const config = EMBEDDED_HEADER_CONFIG[actionType];
  return (
    <div className="evo-dialog-title">
      <Button type="button" link icon="pi pi-angle-double-left" label="Volver a historia clínica" onClick={onBack} />
      <span className="evo-dialog-title__divider" aria-hidden="true" />
      <span className="evo-dialog-title__icon" aria-hidden="true">
        <i className={`pi ${config.icon}`} />
      </span>
      <span className="evo-dialog-title__copy">
        <span className="nv-modal-title">{config.title}</span>
        <span className="nv-help evo-dialog-title__subtitle">{config.subtitle}</span>
      </span>
    </div>
  );
}

function ScoreField({ field, onChange }: { field: EvolutionField; onChange: (field: EvolutionField) => void }) {
  const id = `evolution-score-${field.scoreId}`;
  const label = (
    <label htmlFor={id}>
      {field.unit ? `${field.label} (${field.unit})` : field.label}
      {field.readOnly ? ' (calculado)' : null}
      {field.required ? <> <span className="nv-req">*</span></> : null}
    </label>
  );

  if (field.kind === 'cie10') {
    return <CIE10ScorePanel items={field.cie10Items ?? []} onChange={(items) => onChange({ ...field, cie10Items: items })} />;
  }

  if (field.kind === 'number') {
    return (
      <div className="nv-field evo-score-field">
        {label}
        {/* TODO(design-system): campo calculado no editable; se usa nv-autofilled (readOnly) en vez de disabled. */}
        <InputNumber
          inputId={id}
          value={field.value === null || field.value === '' ? null : Number(field.value.replace(',', '.'))}
          onValueChange={field.readOnly
            ? undefined
            : (event) => onChange({ ...field, value: event.value === null ? null : String(event.value) })}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          minFractionDigits={0}
          maxFractionDigits={field.decimalPrecision ?? 3}
          locale="es-AR"
          useGrouping={false}
          readOnly={field.readOnly}
          className={field.readOnly ? 'nv-autofilled' : undefined}
          tooltip={field.description ?? undefined}
          tooltipOptions={TOOLTIP_OPTIONS}
        />
      </div>
    );
  }

  if (field.kind === 'singleOption') {
    return (
      <div className="nv-field evo-score-field">
        {label}
        <Dropdown
          inputId={id}
          value={field.selectedOptionIds?.[0] ?? null}
          options={field.options}
          optionLabel="label"
          optionValue="scoreOptionId"
          showClear
          placeholder="Seleccionar"
          className="evo-score-field__control"
          panelClassName="nv-scope"
          onChange={(event) => onChange({ ...field, selectedOptionIds: event.value == null ? [] : [event.value] })}
        />
      </div>
    );
  }

  if (field.kind === 'multiOption') {
    return (
      <div className="nv-field evo-score-field">
        {label}
        <MultiSelect
          inputId={id}
          value={field.selectedOptionIds ?? []}
          options={field.options}
          optionLabel="label"
          optionValue="scoreOptionId"
          display="chip"
          placeholder="Seleccionar"
          className="evo-score-field__control"
          panelClassName="nv-scope"
          onChange={(event) => {
            const selected = event.value as number[];
            const previous = field.selectedOptionIds ?? [];
            const newlySelected = selected.filter((optionId) => !previous.includes(optionId));
            const newExclusive = field.options.find((option) =>
              option.deselectOthers && newlySelected.includes(option.scoreOptionId));
            const nextSelection = newExclusive
              ? [newExclusive.scoreOptionId]
              : selected.filter((optionId) => !field.options.some((option) =>
                option.scoreOptionId === optionId && option.deselectOthers));
            onChange({ ...field, selectedOptionIds: nextSelection });
          }}
        />
      </div>
    );
  }

  if (field.kind === 'date' || field.kind === 'datetime') {
    // Input nativo: toma el estilo de .p-inputtext; p-filled lo pinta de gris con valor, como los demás campos.
    return (
      <div className="nv-field evo-score-field">
        {label}
        <input
          id={id}
          className={`p-inputtext p-component evo-score-field__control${field.value ? ' p-filled' : ''}`}
          type={field.kind === 'date' ? 'date' : 'datetime-local'}
          value={field.value ?? ''}
          onChange={(event) => onChange({ ...field, value: event.target.value || null })}
        />
      </div>
    );
  }

  return (
    <div className="nv-field evo-score-field evo-score-field--text">
      {label}
      <InputTextarea
        id={id}
        value={field.value ?? ''}
        onChange={(event) => onChange({ ...field, value: event.target.value })}
        rows={4}
        autoResize
        placeholder={`Escribir ${field.label.toLowerCase()}…`}
        className="evo-score-field__control"
        tooltip={field.description ?? undefined}
        tooltipOptions={TOOLTIP_OPTIONS}
      />
    </div>
  );
}

function EvolutionPanelSection({
  panel,
  expanded,
  onToggle,
  onFieldChange,
  onRemove,
}: {
  panel: EvolutionPanel;
  expanded: boolean;
  onToggle: () => void;
  onFieldChange: (field: EvolutionField) => void;
  onRemove: () => void;
}) {
  const glycemia = panel.fields.find((field) => field.scoreTypeId === GLYCEMIA_SCORE_TYPE)?.value;
  const bodyId = `evolution-panel-${panel.panelTemplateId}`;
  return (
    <section className="evo-section">
      <div
        className="evo-section__header"
        onClick={onToggle}
        onKeyDown={(event) => {
          // Solo el encabezado: Enter o Espacio sobre "Quitar" no pliega la sección (y deja que se quite).
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={expanded ? bodyId : undefined}
      >
        <span className="evo-section__heading">
          <span className="nv-section-title evo-section__title">{panel.title}</span>
          {panel.fields.length > 1 ? <Tag className="nv-tag-neutral evo-section__count" value={`${panel.fields.length} campos`} /> : null}
        </span>
        <span className="evo-section__header-actions">
          <Button
            type="button"
            text
            className="nv-icon"
            icon="pi pi-trash"
            aria-label={`Quitar ${panel.title}`}
            tooltip="Quitar"
            tooltipOptions={TOOLTIP_OPTIONS}
            onClick={(event) => { event.stopPropagation(); onRemove(); }}
          />
          <i className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'} evo-section__chevron`} aria-hidden="true" />
        </span>
      </div>
      {expanded ? (
        <div id={bodyId} className={`evo-section__body${panel.fields.length > 1 ? ' evo-score-grid' : ''}`}>
          {panel.fields.map((field) => {
            if (field.scoreTypeId === INSULIN_SCORE_TYPE && !glycemia?.trim()) return null;
            return <ScoreField key={field.scoreId} field={field} onChange={onFieldChange} />;
          })}
        </div>
      ) : null}
    </section>
  );
}

// Modales de decisión (notificaciones.md §3). PrimeReact 9 no tiene defaultFocus: el foco en la opción segura va con
// autoFocus, en un pie propio.

/** Decisión con la opción segura (secundario, con el foco) y la que avanza (primario, nunca rojo). */
function confirmDecision({ header, message, acceptLabel, rejectLabel, accept }: {
  header: string;
  message: string;
  acceptLabel: string;
  rejectLabel: string;
  accept: () => void;
}) {
  confirmDialog({
    header,
    message,
    className: 'nv-scope nv-confirm',
    footer: (options) => (
      <>
        <Button type="button" className="buttonMain2" outlined label={rejectLabel} onClick={options.reject} autoFocus />
        <Button type="button" className="buttonMain" label={acceptLabel} onClick={options.accept} />
      </>
    ),
    accept,
  });
}

/**
 * Cambios sin guardar: "Seguir editando" (link, a la izquierda, con el foco) y "Salir sin guardar" (secundario).
 * No hay "Guardar y salir": el modal guarda de varias formas (borrador, atendido).
 */
function confirmUnsavedExit(message: string, onExit: () => void) {
  confirmDialog({
    header: '¿Salir sin guardar?',
    message,
    className: 'nv-scope nv-confirm',
    footer: (options) => (
      <>
        <Button type="button" link label="Seguir editando" className="nv-push-left" onClick={options.reject} autoFocus />
        <Button type="button" className="buttonMain2" outlined label="Salir sin guardar" onClick={options.accept} />
      </>
    ),
    accept: onExit,
  });
}

function toSavePanels(panels: EvolutionPanel[]): SavePanelValue[] {
  return panels.map((panel) => ({
    panelTemplateId: panel.panelTemplateId,
    beanName: panel.beanName,
    values: panel.fields.reduce<SaveFieldValue[]>((values, field) => {
      const common = { scoreId: field.scoreId, kind: field.kind };
      if (field.kind === 'cie10' && field.cie10Items.length) {
        values.push({ ...common, cie10Items: field.cie10Items });
      }
      if (field.kind === 'singleOption' || field.kind === 'multiOption') {
        if (field.selectedOptionIds.length) values.push({ ...common, scoreOptionIds: field.selectedOptionIds });
      } else if (field.kind !== 'cie10' && field.value?.trim()) {
        values.push({ ...common, value: field.value });
      }
      return values;
    }, []),
  })).filter((panel) => panel.values.length > 0);
}

export default function MedicalEvolutionModal({
  visible,
  onHide,
  patientId,
  appointmentId,
  servedTime,
  editPatientControlId,
  patientName,
  onAttended,
  onMissed,
  onCancel,
  onEditSaved,
  onBookAppointment,
}: MedicalEvolutionModalProps) {
  const [panels, setPanels] = useState<EvolutionPanel[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [loadedData, setLoadedData] = useState<EvolutionInitData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Errores del back: toast (useNotify). Acá solo queda la validación de obligatorios y si falló la carga.
  const [validationError, setValidationError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<EvolutionPanel | null>(null);
  const [clinicalHistoryVisible, setClinicalHistoryVisible] = useState(false);
  const [embeddedAction, setEmbeddedAction] = useState<ClinicalHistoryAction | null>(null);
  const [odontogramDirty, setOdontogramDirty] = useState(false);
  const [timerStart, setTimerStart] = useState(() => Date.now());
  const [draftPatientControlId, setDraftPatientControlId] = useState<number | null>(null);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const initialSnapshot = useRef('[]');
  const isEditMode = editPatientControlId != null && editPatientControlId > 0;
  const notify = useNotify();

  useEffect(() => {
    if (!visible) return;
    if (!isEditMode && !appointmentId) return;
    let active = true;
    setLoading(true);
    setValidationError(null);
    setLoadFailed(false);
    setDraftPatientControlId(null);
    setMinimized(false);
    if (!isEditMode) {
      setTimerStart(servedTime ? new Date(servedTime).getTime() : Date.now());
    }

    const initPromise = isEditMode && patientId
      ? getEditEvolutionInit(patientId, editPatientControlId!)
      : getEvolutionInit(appointmentId!);

    initPromise
      .then((data) => {
        if (!active) return;
        const nextPanels = clonePanels(data.panels ?? []);
        setLoadedData(data);
        setPanels(nextPanels);
        setExpandedSections(nextPanels[0] ? { [nextPanels[0].panelTemplateId]: true } : {});
        initialSnapshot.current = JSON.stringify(nextPanels);
      })
      .catch((requestError) => {
        if (active) {
          setLoadFailed(true);
          notify.error(parseApiError(requestError, 'No se pudo cargar la evolución. Intentá de nuevo.'));
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  // notify es estable (useMemo sobre dispatch): no dispara recargas.
  }, [visible, appointmentId, editPatientControlId, isEditMode, patientId, notify]);

  useEffect(() => {
    if (!visible) {
      setClinicalHistoryVisible(false);
      setEmbeddedAction(null);
      setOdontogramDirty(false);
    }
  }, [visible]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dirty = useMemo(() => JSON.stringify(panels) !== initialSnapshot.current, [panels, snapshotVersion]);
  const availablePanels = useMemo(() => {
    const present = new Set(panels.map((panel) => panel.panelTemplateId));
    return (loadedData?.availablePanels ?? []).filter((panel) => !present.has(panel.panelTemplateId));
  }, [loadedData?.availablePanels, panels]);

  const requestClose = useCallback(() => {
    if (!dirty && !odontogramDirty) { onHide(); return; }
    confirmUnsavedExit('Los cambios que hiciste se van a perder.', onHide);
  }, [dirty, odontogramDirty, onHide]);

  const updateField = useCallback((panelTemplateId: number, nextField: EvolutionField) => {
    setPanels((current) => current.map((panel) => {
      if (panel.panelTemplateId !== panelTemplateId) return panel;
      let fields = panel.fields.map((field) => field.scoreId === nextField.scoreId ? nextField : field);
      if (nextField.scoreTypeId === SYSTOLIC_SCORE_TYPE || nextField.scoreTypeId === DIASTOLIC_SCORE_TYPE) {
        const systolic = fields.find((field) => field.scoreTypeId === SYSTOLIC_SCORE_TYPE)?.value;
        const diastolic = fields.find((field) => field.scoreTypeId === DIASTOLIC_SCORE_TYPE)?.value;
        const calculated = systolic?.trim() && diastolic?.trim()
          ? String(Math.ceil((Number(systolic) + (2 * Number(diastolic))) / 3))
          : null;
        fields = fields.map((field) => field.scoreTypeId === PAM_SCORE_TYPE ? { ...field, value: calculated } : field);
      }
      if (nextField.scoreTypeId === GLYCEMIA_SCORE_TYPE && !nextField.value?.trim()) {
        fields = fields.map((field) => field.scoreTypeId === INSULIN_SCORE_TYPE ? { ...field, value: null } : field);
      }
      return { ...panel, fields: calculatePanelFields(panel.beanName, fields) };
    }));
  }, []);

  const validateRequiredFields = useCallback((): string | null => {
    for (const panel of panels) {
      for (const field of panel.fields) {
        if (!field.required) continue;
        const complete = field.kind === 'singleOption' || field.kind === 'multiOption'
          ? field.selectedOptionIds.length > 0
          : field.kind === 'cie10'
            ? field.cie10Items.length > 0
            : Boolean(field.value?.trim());
        if (!complete) return `Completá “${field.label}” en ${panel.title}.`;
      }
    }
    return null;
  }, [panels]);

  const handleAttended = useCallback(async () => {
    if (!appointmentId || !loadedData) return;
    const missingField = validateRequiredFields();
    if (missingField) {
      setValidationError(missingField);
      return;
    }
    setSaving(true);
    setValidationError(null);
    try {
      await saveEvolution(
        appointmentId,
        loadedData.scoreTemplateId,
        loadedData.previousPatientControlId,
        toSavePanels(panels),
        draftPatientControlId,
      );
      initialSnapshot.current = JSON.stringify(panels);
      onAttended?.();
    } catch (requestError) {
      notify.error(parseApiError(requestError, 'No se pudo guardar la evolución. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  }, [appointmentId, loadedData, draftPatientControlId, onAttended, panels, validateRequiredFields, notify]);

  const handleSaveDraft = useCallback(async () => {
    if (!appointmentId || !loadedData) return;
    setSaving(true);
    setValidationError(null);
    try {
      const result = await saveDraft(
        appointmentId,
        loadedData.scoreTemplateId,
        loadedData.previousPatientControlId,
        toSavePanels(panels),
        draftPatientControlId,
      );
      if (result.patientControlId) {
        setDraftPatientControlId(result.patientControlId);
      }
      initialSnapshot.current = JSON.stringify(panels);
      setSnapshotVersion((v) => v + 1);
      notify.success('Borrador guardado');
    } catch (requestError) {
      notify.error(parseApiError(requestError, 'No se pudo guardar el borrador. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  }, [appointmentId, loadedData, draftPatientControlId, panels, notify]);

  const handleMissed = useCallback(() => {
    if (!appointmentId) return;
    confirmDecision({
      header: '¿Marcar al paciente como ausente?',
      message: 'Se va a registrar que el paciente no se presentó a la atención.',
      acceptLabel: 'Marcar ausente',
      rejectLabel: 'Cancelar',
      accept: async () => {
        setSaving(true);
        setValidationError(null);
        try {
          await markAsMissed(appointmentId);
          onMissed?.();
        } catch (requestError) {
          notify.error(parseApiError(requestError, 'No se pudo marcar al paciente como ausente. Intentá de nuevo.'));
        } finally {
          setSaving(false);
        }
      },
    });
  }, [appointmentId, onMissed, notify]);

  const handleCancel = useCallback(() => {
    if (!appointmentId) return;
    confirmDecision({
      header: '¿Devolver al paciente a la cola de espera?',
      message: 'Los datos que no guardaste se van a perder.',
      acceptLabel: 'Devolver a la cola',
      rejectLabel: 'Continuar atención',
      accept: async () => {
        setSaving(true);
        setValidationError(null);
        try {
          await returnToQueue(appointmentId);
          onCancel?.();
        } catch (requestError) {
          notify.error(parseApiError(requestError, 'No se pudo devolver al paciente a la cola. Intentá de nuevo.'));
        } finally {
          setSaving(false);
        }
      },
    });
  }, [appointmentId, onCancel, notify]);

  const handleEditSave = useCallback(async () => {
    if (!patientId || !editPatientControlId || !loadedData) return;
    setSaving(true);
    setValidationError(null);
    try {
      await saveEditEvolution(
        patientId,
        editPatientControlId,
        loadedData.scoreTemplateId,
        loadedData.previousPatientControlId,
        toSavePanels(panels),
      );
      initialSnapshot.current = JSON.stringify(panels);
      setSnapshotVersion((v) => v + 1);
      onEditSaved?.();
    } catch (requestError) {
      notify.error(parseApiError(requestError, 'No se pudieron guardar los cambios. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  }, [patientId, editPatientControlId, loadedData, panels, onEditSaved, notify]);

  const [editTarget, setEditTarget] = useState<{ patientControlId: number } | null>(null);
  const [hcRefreshToken, setHcRefreshToken] = useState(0);
  const [editLog, setEditLog] = useState<EditLogEntry[] | null>(null);

  const handleAction = useCallback((action: ClinicalHistoryAction) => {
    if (action.type === 'editEvolution') {
      setEditTarget({ patientControlId: action.patientControlId });
      return;
    }
    if (action.type === 'viewEditLog' && patientId) {
      getEditLog(patientId, action.patientControlId)
        .then((log) => {
          if (log.length > 0) {
            setEditLog(log);
          }
        });
      return;
    }
    setOdontogramDirty(false);
    setEmbeddedAction(action);
  }, [patientId]);

  const handleEmbeddedBack = useCallback(() => {
    const closeAction = () => {
      setOdontogramDirty(false);
      setEmbeddedAction(null);
    };
    if (embeddedAction?.type !== 'odontogram' || !odontogramDirty) {
      closeAction();
      return;
    }
    confirmUnsavedExit('Los cambios que hiciste en el odontograma se van a perder.', closeAction);
  }, [embeddedAction?.type, odontogramDirty]);

  const addPanel = useCallback(() => {
    if (!selectedPanel) return;
    const panel = clonePanels([selectedPanel])[0];
    setPanels((current) => [...current, { ...panel, order: current.length }]);
    setExpandedSections((current) => ({ ...current, [panel.panelTemplateId]: true }));
    setSelectedPanel(null);
  }, [selectedPanel]);

  // Pie propio (nv-scope): el Dialog no lo lleva en la raíz porque embebe vistas que no se migran.
  const footer = (
    <div className="nv-scope evo-footer">
      {isEditMode ? null : (
        <div className="evo-footer__left">
          {loadedData ? <AdministrativeRecordStatus record={loadedData.administrativeRecord} /> : null}
          <span className="nv-help evo-timer">
            <i className="pi pi-clock" aria-hidden="true" />
            <span className="nv-sr-only">Tiempo de atención</span>
            <LiveTimer since={timerStart} />
          </span>
        </div>
      )}
      {isEditMode ? (
        <div className="evo-footer__right">
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onHide} disabled={saving || loading} />
          <Button type="button" className="buttonMain" label="Guardar cambios" onClick={handleEditSave} loading={saving} disabled={loading} />
        </div>
      ) : (
        <div className="evo-footer__right">
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={handleCancel} disabled={saving || loading} />
          <Button type="button" className="buttonMain2" outlined label="Guardar borrador" onClick={handleSaveDraft} disabled={saving || loading} />
          <Button type="button" className="buttonMain2" outlined label="Ausente" onClick={handleMissed} disabled={saving || loading} />
          <Button type="button" className="buttonMain" label="Atendido" onClick={handleAttended} loading={saving} disabled={loading} />
        </div>
      )}
    </div>
  );

  if (visible && minimized && !isEditMode) {
    const name = patientName ?? 'Paciente';
    // TODO(design-system): Novarium no define el aviso flotante de una atención minimizada; se usa la complementaria
    // (trae de vuelta algo sin confirmar ni cerrar) con la sombra de los overlays.
    return (
      <div className="nv-scope evo-minimized-bubble">
        <Button
          type="button"
          className="buttonMain3"
          aria-label={`Restaurar evolución médica de ${name}`}
          tooltip="Restaurar evolución médica"
          tooltipOptions={TOOLTIP_OPTIONS}
          onClick={() => setMinimized(false)}
        >
          <i className="pi pi-clock" aria-hidden="true" />
          <span className="evo-minimized-bubble__timer"><LiveTimer since={timerStart} /></span>
          <span className="evo-minimized-bubble__name">{name}</span>
          <i className="pi pi-window-maximize" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* La raíz no lleva nv-scope: embebe vistas compartidas que no se migran (RecipeView, NewStudyOrderView,
          PatientClinicalPanelById, PatientClinicalHistoryView…). Lo llevan el header, el pie y los bloques propios. */}
      <Dialog
        visible={visible}
        onHide={requestClose}
        header={
          embeddedAction
            ? <EmbeddedActionDialogHeader actionType={embeddedAction.type} onBack={handleEmbeddedBack} />
            : isEditMode
              ? <EvolutionDialogHeader continuing={true} editMode />
              : <EvolutionDialogHeader continuing={Boolean(loadedData?.existingAmbulatory)} onMinimize={() => setMinimized(true)} />
        }
        headerClassName="nv-scope"
        footer={embeddedAction ? undefined : footer}
        modal
        className={`medical-evolution-modal${embeddedAction ? ' medical-evolution-modal--history' : ''}`}
        style={{ width: 'min(1100px, 96vw)' }}
        closable={!saving}
        closeOnEscape={!saving}
        draggable={false}
        maximized
      >
        {embeddedAction && patientId !== null ? (
          (embeddedAction.type === 'copyRecipe' || embeddedAction.type === 'newRecipe') ? (
            <RecipeView
              embeddedPatientId={patientId}
              embeddedDigitalRpId={embeddedAction.type === 'copyRecipe' ? embeddedAction.healthPatientServiceId : undefined}
              onExitFlow={handleEmbeddedBack}
            />
          ) : embeddedAction.type === 'newMedicalIndication' ? (
            <MedicalIndicationView
              embeddedPatientId={patientId}
              embeddedProfessionalId={embeddedAction.professionalId}
              onExitFlow={handleEmbeddedBack}
              onSwitchToStudyOrder={() => {
                setEmbeddedAction({ type: 'newStudyOrder', professionalId: embeddedAction.professionalId });
              }}
            />
          ) : embeddedAction.type === 'loadStudyResult' ? (
            <StudyResultWizard
              visible={true}
              onHide={handleEmbeddedBack}
              healthPatientServiceId={embeddedAction.healthPatientServiceId}
              patientId={patientId}
              onSaved={handleEmbeddedBack}
              professionalId={embeddedAction.professionalId ?? undefined}
              professionalName={embeddedAction.professionalName ?? undefined}
              studyOrderId={embeddedAction.studyOrderId}
            />
          ) : embeddedAction.type === 'odontogram' ? (
            <OdontogramView
              patientId={patientId}
              initialPatientControlId={embeddedAction.patientControlId}
              onDirtyChange={setOdontogramDirty}
            />
          ) : (
            <NewStudyOrderView
              setActiveView={() => {}}
              setSubActiveView={() => {}}
              setPrevActiveView={() => {}}
              setPrevSubActiveView={() => {}}
              onExitFlow={handleEmbeddedBack}
              initialSelectedPatient={{ medereentity: patientId }}
              initialStep={EStudyOrderSteps.PRESCRIPTIONSTEP}
              embeddedPatientId={patientId}
              embeddedCopyFromHpsId={embeddedAction.type === 'copyStudyOrder' ? embeddedAction.healthPatientServiceId : undefined}
              embeddedProfessionalId={embeddedAction.professionalId}
            />
          )
        ) : (
          <div className={`evo-workspace${clinicalHistoryVisible ? ' evo-workspace--with-history' : ''}`}>
            <section className="evo-editor-pane" aria-label="Editor de evolución médica">
              {patientId !== null ? (
                <div className="evo-clinical-context">
                  <div className="nv-scope evo-clinical-context__actions">
                    <Button
                      type="button"
                      className="buttonMain3"
                      icon={clinicalHistoryVisible ? 'pi pi-eye-slash' : 'pi pi-book'}
                      label={clinicalHistoryVisible ? 'Ocultar historia clínica' : 'Ver historia clínica'}
                      onClick={() => setClinicalHistoryVisible((current) => !current)}
                      aria-expanded={clinicalHistoryVisible}
                    />
                    {onBookAppointment && patientId !== null && (
                      <Button
                        type="button"
                        className="buttonMain3"
                        icon="pi pi-calendar-plus"
                        label="Sacar turno"
                        onClick={() => onBookAppointment(patientId)}
                      />
                    )}
                  </div>
                  <PatientClinicalPanelById patientId={patientId} />
                </div>
              ) : null}

              <div className="nv-scope evo-editor">
                {loading ? (
                  // TODO(design-system): el spinner tendría que ir en un solo color (--teal-700); nv-loading no lo pinta.
                  <div className="evo-loading">
                    <div className="nv-loading" role="status">
                      <ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…
                    </div>
                  </div>
                ) : (
                  <>
                    {validationError ? (
                      <div role="alert">
                        <FieldError id="evolution-validation-error" message={validationError} />
                      </div>
                    ) : null}

                    {availablePanels.length > 0 ? (
                      <div className="nv-form-row">
                        <div className="nv-field nv-w-m">
                          <label htmlFor="evolution-add-panel">Plantilla</label>
                          <Dropdown
                            inputId="evolution-add-panel"
                            value={selectedPanel}
                            options={availablePanels}
                            optionLabel="title"
                            onChange={(event) => setSelectedPanel(event.value)}
                            placeholder="Seleccionar"
                            panelClassName="nv-scope"
                          />
                        </div>
                        <div className="nv-field-inline-action">
                          <Button
                            type="button"
                            className="buttonMain2"
                            outlined
                            icon="pi pi-plus-circle"
                            label="Agregar plantilla"
                            onClick={addPanel}
                            disabled={!selectedPanel}
                            tooltip={selectedPanel ? undefined : 'Elegí una plantilla primero'}
                            tooltipOptions={{ ...TOOLTIP_OPTIONS, showOnDisabled: true }}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="evo-sections">
                      {panels.map((panel) => (
                        <EvolutionPanelSection
                          key={panel.panelTemplateId}
                          panel={panel}
                          expanded={Boolean(expandedSections[panel.panelTemplateId])}
                          onToggle={() => setExpandedSections((current) => ({ ...current, [panel.panelTemplateId]: !current[panel.panelTemplateId] }))}
                          onFieldChange={(field) => updateField(panel.panelTemplateId, field)}
                          onRemove={() => setPanels((current) => current.filter((item) => item.panelTemplateId !== panel.panelTemplateId))}
                        />
                      ))}
                      {!panels.length && !loadFailed ? (
                        <p className="nv-note">
                          <i className="pi pi-info-circle" aria-hidden="true" /> La plantilla no tiene paneles compatibles con esta evolución.
                        </p>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </section>

            {clinicalHistoryVisible && patientId !== null ? (
              <aside className="evo-history-pane" aria-label="Historia clínica del paciente">
                <div className="nv-scope evo-history-pane__header">
                  <div className="evo-dialog-title">
                    <span className="evo-dialog-title__icon" aria-hidden="true">
                      <i className="pi pi-book" />
                    </span>
                    <span className="evo-dialog-title__copy">
                      <span className="nv-section-title">Historia clínica</span>
                      <span className="nv-help evo-dialog-title__subtitle">Podés consultar la HC mientras completás la evolución</span>
                    </span>
                  </div>
                  <Button
                    type="button"
                    text
                    className="nv-icon"
                    icon="pi pi-times"
                    aria-label="Cerrar historia clínica"
                    tooltip="Cerrar historia clínica"
                    tooltipOptions={TOOLTIP_OPTIONS}
                    onClick={() => setClinicalHistoryVisible(false)}
                  />
                </div>
                <div className="evo-history-pane__content">
                  <PatientClinicalHistoryView patientId={patientId} onAction={handleAction} refreshToken={hcRefreshToken} />
                </div>
              </aside>
            ) : null}
          </div>
        )}
      </Dialog>

      <Dialog
        visible={editLog !== null}
        onHide={() => setEditLog(null)}
        header="Historial de ediciones"
        modal
        className="nv-scope nv-dialog"
        style={{ width: 'min(800px, 96vw)' }}
        draggable={false}
      >
        <div className="nv-dialog-body">
          {editLog?.map((entry, idx) => (
            <Fragment key={entry.patientControlId}>
              <article className="evo-log-entry">
                <div className="evo-log-entry__head">
                  <span className={entry.deleted ? 'nv-muted evo-log-entry__title' : 'nv-strong'}>
                    {entry.deleted ? 'Versión anterior' : 'Versión actual'} — {entry.userFullName ?? 'Sin datos'}
                  </span>
                  <span className="nv-help">{entry.recordDate ?? '—'}</span>
                </div>
                {entry.panels.map((panel) => (
                  <p key={panel.panelTemplateId} className="evo-log-entry__panel">
                    <span className="nv-strong">{panel.title}:</span>{' '}
                    {panel.fields
                      .filter((f) => f.value?.trim())
                      .map((f) => `${f.label}: ${f.value}`)
                      .join(' | ') || '—'}
                  </p>
                ))}
              </article>
              {idx < editLog.length - 1 ? <hr className="nv-divider" /> : null}
            </Fragment>
          ))}
        </div>
      </Dialog>

      {editTarget && patientId !== null ? (
        <MedicalEvolutionModal
          visible
          onHide={() => setEditTarget(null)}
          patientId={patientId}
          appointmentId={null}
          editPatientControlId={editTarget.patientControlId}
          onEditSaved={() => { setEditTarget(null); setHcRefreshToken((v) => v + 1); }}
        />
      ) : null}
    </>
  );
}
